import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import type { Finding } from '../cms/shared';
import {
  applyAtFocus,
  focusEmptyBullets,
  focusedRange,
  removeEmptyBullets,
  setFocusedRange,
} from './copyEditFocus';
import {
  isEmptyBullets,
  LINE_MARKER,
  locateInDoc,
  locateQuote,
  suggestionText,
  type CopyEditField,
  type DraftTexts,
} from './copyEditQuote';

/**
 * The floating box over a copy-edit finding's text (backlog f2261d58): the page
 * moves to the text, highlights it, and Apply changes it in the edit. Nothing
 * reaches the working copy until Save.
 */

const GAP = 10;
const MARGIN = 12;
const WIDTH = 340;

export const scrollBehavior = (): ScrollBehavior =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

/**
 * The box: above the text, or under it when there's no room above, and it stays
 * on the text while the page moves. `watch` is for anything else that can move it.
 */
export function CopyEditBox({
  finding,
  rect,
  focusApply,
  watch,
  hint,
  onApply,
  onSkip,
  onClose,
}: {
  finding: Finding;
  /** Set when the words couldn't be found: what to tell the person to look for. */
  hint?: string;
  rect: () => { top: number; bottom: number; left: number } | null;
  /** Leave the focus alone when a field holds the selected words. */
  focusApply: boolean;
  watch?: (place: () => void) => () => void;
  onApply: () => void;
  /** On to the next finding, leaving this one; none when it's the last. */
  onSkip?: () => void;
  onClose: () => void;
}) {
  const box = useRef<HTMLDivElement>(null);
  const applyBtn = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const rectRef = useRef(rect);
  rectRef.current = rect;

  useLayoutEffect(() => {
    const place = () => {
      const at = rectRef.current();
      if (!at) return;
      const height = box.current?.offsetHeight ?? 0;
      const above = at.top - height - GAP;
      const top = above >= MARGIN ? above : at.bottom + GAP;
      setPos({
        top: Math.max(MARGIN, Math.min(top, window.innerHeight - height - MARGIN)),
        left: Math.max(MARGIN, Math.min(at.left, window.innerWidth - WIDTH - MARGIN)),
      });
    };
    place();
    if (focusApply) applyBtn.current?.focus({ preventScroll: true });
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    const unwatch = watch?.(place);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      unwatch?.();
      document.removeEventListener('keydown', onKey);
    };
    // Once: `key` remounts this for the next finding.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bullets = isEmptyBullets(finding);
  const text = suggestionText(finding);
  const canApply = !hint && (bullets || text !== null);
  // The check's own line, for a person hunting for it: not the "- " or "## " it adds.
  const look = (finding.line || '').trim().replace(LINE_MARKER, '').trim();
  return createPortal(
    <div
      ref={box}
      className="ink-ce-pop"
      role="dialog"
      aria-label="Copy edit suggestion"
      style={{ top: pos.top, left: pos.left, width: WIDTH }}
      // Clicking the box leaves the selection in a field where it is.
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="ink-ce-pop-change">
        {hint ? <div className="ink-ce-pop-lost">Couldn’t find this in the text</div> : null}
        {bullets ? (
          'Empty bullets'
        ) : (
          <>
            <del>{finding.quote}</del>
            {text ? (
              <>
                {' → '}
                <ins>{text}</ins>
              </>
            ) : null}
          </>
        )}
      </div>
      {finding.message ? <p className="ink-ce-pop-why">{finding.message}</p> : null}
      {hint ? (
        <p className="ink-ce-pop-why">
          {look && look !== (finding.quote || '').trim() ? (
            <>
              Look for: <q>{look.length > 160 ? `${look.slice(0, 160)}…` : look}</q>{' '}
            </>
          ) : null}
          {hint}
        </p>
      ) : null}
      <div className="ink-ce-pop-actions">
        {canApply ? (
          <button
            ref={applyBtn}
            type="button"
            className="ink-btn ink-btn-primary"
            onClick={onApply}
          >
            {bullets ? 'Remove them' : text ? 'Apply' : 'Remove it'}
          </button>
        ) : null}
        {onSkip ? (
          <button type="button" className="ink-btn" onClick={onSkip}>
            Skip
          </button>
        ) : null}
        <button type="button" className="ink-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>,
    document.body
  );
}

export function CopyEditPopover({
  finding,
  editor,
  bodyLocked,
  texts,
  onApplyField,
  onApplied,
  onSkip,
  onClose,
}: {
  finding: Finding;
  /** The body's editor, once it's ready. */
  editor: Editor | null;
  /** The body has formatting the editor can't keep, so it can't change here. */
  bodyLocked: boolean;
  texts: DraftTexts;
  onApplyField: (field: CopyEditField, value: string) => void;
  /** It's in: on to the next finding, or close. */
  onApplied: () => void;
  onSkip?: () => void;
  onClose: () => void;
}) {
  const quote = finding.quote || '';
  const field = finding.path && finding.path in texts ? (finding.path as CopyEditField) : null;
  const [ready, setReady] = useState(false);
  // Not found: the box stays, and says what to look for.
  const [hint, setHint] = useState('');

  const fieldEl = useCallback(
    () =>
      document.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[data-ce-field="${field}"]`),
    [field]
  );

  const rect = () => {
    if (field) return fieldEl()?.getBoundingClientRect() ?? null;
    if (hint) return document.querySelector('.ink-be')?.getBoundingClientRect() ?? null;
    if (!editor || editor.isDestroyed) return null;
    const range = focusedRange(editor);
    return range ? editor.view.coordsAtPos(range.from) : null;
  };

  // Find the text, move to it and highlight it. Waits for the body's editor when
  // the click also opened Edit.
  useEffect(() => {
    if (field) {
      const el = fieldEl();
      const hit = el ? locateQuote(el.value, quote) : null;
      if (!el || !hit) {
        setHint('It may have changed since the check. Run copy edit again to refresh this list.');
        setReady(true);
        return undefined;
      }
      el.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
      el.focus({ preventScroll: true });
      el.setSelectionRange(hit.from, hit.to);
      setReady(true);
      return undefined;
    }
    const lost = (why: string) => {
      document
        .querySelector('.ink-be')
        ?.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
      setHint(why);
      setReady(true);
    };
    if (bodyLocked) {
      lost('This body has formatting the editor can’t keep, so ask your agent to change it.');
      return undefined;
    }
    if (!editor || editor.isDestroyed) return undefined;
    const bullets = isEmptyBullets(finding);
    const hit = bullets ? null : locateInDoc(editor.state.doc, quote, finding.line);
    if (bullets ? !focusEmptyBullets(editor) : !hit) {
      lost(
        bullets
          ? 'There are no empty bullets in the text now. Run copy edit again to refresh this list.'
          : 'It may have changed since the check, or sit in a table or box the editor can’t open.'
      );
      return undefined;
    }
    if (hit) setFocusedRange(editor, hit);
    const { node } = editor.view.domAtPos(focusedRange(editor)?.from ?? 0);
    const el = node instanceof Element ? node : node.parentElement;
    el?.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
    setReady(true);
    return () => {
      if (!editor.isDestroyed) setFocusedRange(editor, null);
    };
    // Once per finding: `key` remounts this for the next one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, bodyLocked]);

  const apply = () => {
    const suggestion = suggestionText(finding) ?? '';
    if (isEmptyBullets(finding) && editor) {
      removeEmptyBullets(editor);
    } else if (field) {
      const el = fieldEl();
      // The field may have been typed in since: find the words again.
      const hit = el ? locateQuote(el.value, quote) : null;
      if (el && hit)
        onApplyField(field, el.value.slice(0, hit.from) + suggestion + el.value.slice(hit.to));
    } else if (editor) {
      applyAtFocus(editor, suggestion);
    }
    onApplied();
  };

  if (!ready) return null;
  return (
    <CopyEditBox
      finding={finding}
      hint={hint || undefined}
      rect={rect}
      focusApply={!field && !hint}
      // If the highlighted words are deleted there's nothing left to apply to.
      watch={(place) => {
        if (field || hint || !editor) return () => undefined;
        const onTransaction = () => {
          if (focusedRange(editor)) place();
          else onClose();
        };
        editor.on('transaction', onTransaction);
        return () => {
          editor.off('transaction', onTransaction);
        };
      }}
      onApply={apply}
      onSkip={onSkip}
      onClose={onClose}
    />
  );
}
